// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2)
// version  : 2.0
// updated  : 2026-06-20
// protected: NO  -> v2.0: tambah "Scan via Kamera HP" (html5-qrcode, continuous,
//            back camera, beep+getar+torch). Input ketik manual & scan-gun TETAP jalan.
//            Hanya enhancement FRONTEND; tidak menyentuh backend/ledger/QR generator.
// =============================================================================
// Input batch QR (scan-gun friendly: ketik/scan + Enter) + kamera HP. Dipakai modul
// Penerimaan Gudang / Transfer / Pengiriman / Retur / Compliment (lewat QrBatchInput).
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScanLine, Plus, X, Camera, Zap, ZapOff, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Html5Qrcode } from "html5-qrcode";

// Normalisasi hasil scan -> kode item bersih.
//  - kode mentah (JUAL-AFC-260619-0001) dipakai langsung
//  - bila URL -> ambil query param relevan / segmen terakhir path
//  - trim spasi; uppercase bila format kode (alfanumerik + - _)
export const parseQr = (raw) => {
  let q = (raw || "").trim();
  if (!q) return "";
  if (/^https?:\/\//i.test(q)) {
    try {
      const u = new URL(q);
      const p =
        u.searchParams.get("code") ||
        u.searchParams.get("qr") ||
        u.searchParams.get("item") ||
        u.searchParams.get("c");
      if (p) {
        q = p;
      } else {
        const segs = u.pathname.split("/").filter(Boolean);
        if (segs.length) q = segs[segs.length - 1];
      }
    } catch (_) {
      /* bukan URL valid -> pakai mentah */
    }
  }
  q = q.trim();
  if (/^[a-z0-9_-]+$/i.test(q)) q = q.toUpperCase();
  return q;
};

const beep = () => {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g);
    g.connect(ctx.destination);
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.setValueAtTime(0.12, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.15);
    o.start();
    o.stop(ctx.currentTime + 0.16);
    o.onended = () => { try { ctx.close(); } catch (_) {} };
  } catch (_) {
    /* noop */
  }
};

const vibrate = (ms = 60) => {
  try { if (navigator.vibrate) navigator.vibrate(ms); } catch (_) {}
};

export const QrBatchInput = ({ value, onChange, placeholder = "Scan / ketik QR lalu Enter", testid = "qr-batch" }) => {
  const [text, setText] = useState("");
  const ref = useRef(null);

  // ---- Kamera HP ----
  const [camOpen, setCamOpen] = useState(false);
  const [camErr, setCamErr] = useState("");
  const [starting, setStarting] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [sessionCount, setSessionCount] = useState(0);

  const qrRef = useRef(null); // instance Html5Qrcode
  const valueRef = useRef(value); // value terbaru utk callback (hindari stale closure)
  const lastRef = useRef({ code: "", t: 0 });
  const regionId = useRef(`qr-cam-${Math.random().toString(36).slice(2, 9)}`).current;
  useEffect(() => { valueRef.current = value; }, [value]);

  const add = () => {
    // P2 fix: pakai valueRef (sinkron) -> hindari STALE closure saat scanner cepat
    // mengirim banyak QR + Enter beruntun (sebelumnya QR sebelumnya bisa tertimpa).
    // Juga dukung banyak kode sekaligus (paste/scan): pisah spasi/baris/koma/semicolon/tab.
    const tokens = (text || "").split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
    if (tokens.length) {
      const merged = [...(valueRef.current || [])];
      let added = 0, dups = 0;
      for (const t of tokens) {
        if (merged.includes(t)) { dups += 1; continue; }
        merged.push(t); added += 1;
      }
      if (added) { valueRef.current = merged; onChange(merged); }
      else if (dups) toast.info("QR sudah ada di daftar");
    }
    setText("");
    ref.current?.focus();
  };
  const remove = (q) => {
    const next = (valueRef.current || []).filter((x) => x !== q);
    valueRef.current = next;
    onChange(next);
  };

  const handleDecoded = useCallback((decodedText) => {
    const now = Date.now();
    const code = parseQr(decodedText);
    if (!code) return;
    // guard frame: kode sama dalam 1.5s diabaikan (QR masih di depan kamera)
    if (lastRef.current.code === code && now - lastRef.current.t < 1500) return;
    lastRef.current = { code, t: now };
    if (valueRef.current.includes(code)) {
      toast.info(`QR sudah ada: ${code}`);
      return;
    }
    onChange([...valueRef.current, code]);
    setSessionCount((c) => c + 1);
    beep();
    vibrate();
  }, [onChange]);

  const stopCamera = useCallback(async () => {
    const inst = qrRef.current;
    qrRef.current = null;
    if (inst) {
      try { await inst.stop(); } catch (_) {}
      try { await inst.clear(); } catch (_) {}
    }
  }, []);

  const startCamera = useCallback(async () => {
    await stopCamera(); // pastikan bersih bila re-entrant
    setCamErr("");
    setStarting(true);
    setTorchOn(false);
    setTorchSupported(false);
    try {
      const inst = new Html5Qrcode(regionId, { verbose: false });
      qrRef.current = inst;
      const config = {
        fps: 10,
        qrbox: (vw, vh) => {
          const m = Math.floor(Math.min(vw, vh) * 0.7);
          return { width: m, height: m };
        },
        aspectRatio: 1.0,
      };
      await inst.start({ facingMode: "environment" }, config, handleDecoded, () => {});
      try {
        const caps = inst.getRunningTrackCapabilities();
        if (caps && caps.torch) setTorchSupported(true);
      } catch (_) { /* torch tidak didukung */ }
    } catch (e) {
      const msg = String((e && (e.message || e.name)) || "");
      if (/permission|denied|NotAllowed/i.test(msg))
        setCamErr("Akses kamera ditolak. Izinkan kamera di pengaturan browser, lalu coba lagi.");
      else if (/NotFound|no camera|Requested device|NotReadable|in use/i.test(msg))
        setCamErr("Kamera tidak ditemukan / sedang dipakai aplikasi lain.");
      else
        setCamErr("Tidak bisa membuka kamera. Pastikan perangkat punya kamera & izin diberikan.");
      await stopCamera();
    } finally {
      setStarting(false);
    }
  }, [handleDecoded, stopCamera, regionId]);

  // Buka/tutup lifecycle kamera
  useEffect(() => {
    if (camOpen) {
      setSessionCount(0);
      // tunggu konten dialog (region div) ter-mount
      const t = setTimeout(() => { startCamera(); }, 180);
      return () => clearTimeout(t);
    }
    stopCamera();
    return undefined;
  }, [camOpen, startCamera, stopCamera]);

  // Cleanup saat unmount
  useEffect(() => () => { stopCamera(); }, [stopCamera]);

  const toggleTorch = async () => {
    const inst = qrRef.current;
    if (!inst) return;
    const next = !torchOn;
    try {
      await inst.applyVideoConstraints({ advanced: [{ torch: next }] });
      setTorchOn(next);
    } catch (_) {
      setTorchSupported(false);
      toast.error("Senter tidak didukung perangkat ini.");
    }
  };

  const retry = () => {
    setCamErr("");
    setTimeout(() => { startCamera(); }, 150);
  };

  return (
    <div data-testid={testid}>
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[160px]">
          <ScanLine className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={ref}
            value={text}
            placeholder={placeholder}
            className="pl-9"
            data-testid={`${testid}-input`}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          />
        </div>
        <Button type="button" variant="outline" onClick={add} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
        <Button type="button" variant="secondary" className="shrink-0" onClick={() => setCamOpen(true)} data-testid={`${testid}-scan-cam`}>
          <Camera className="w-4 h-4 sm:mr-1.5" />
          <span className="hidden sm:inline">Scan via Kamera</span>
        </Button>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {value.map((q) => (
            <Badge key={q} variant="secondary" className="font-mono gap-1 py-1">
              {q}
              <button type="button" onClick={() => remove(q)} data-testid={`${testid}-remove`}>
                <X className="w-3 h-3" />
              </button>
            </Badge>
          ))}
          <Badge variant="outline">{value.length} item</Badge>
        </div>
      )}

      <Dialog open={camOpen} onOpenChange={setCamOpen}>
        <DialogContent className="max-w-md p-0 overflow-hidden gap-0" data-testid={`${testid}-cam-dialog`}>
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Camera className="w-5 h-5" />Scan QR via Kamera
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 pb-4 space-y-3">
            {camErr ? (
              <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive flex gap-2" data-testid={`${testid}-cam-error`}>
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <div className="space-y-2">
                  <p>{camErr}</p>
                  <Button size="sm" variant="outline" onClick={retry} data-testid={`${testid}-cam-retry`}>Coba Lagi</Button>
                </div>
              </div>
            ) : (
              <div className="relative">
                <div id={regionId} className="w-full rounded-xl overflow-hidden bg-black aspect-square" />
                {starting && (
                  <div className="absolute inset-0 flex items-center justify-center text-white/80 gap-2 text-sm">
                    <Loader2 className="w-5 h-5 animate-spin" />Memulai kamera…
                  </div>
                )}
              </div>
            )}

            {!camErr && (
              <p className="text-xs text-muted-foreground text-center">
                Arahkan kamera ke QR. Scan beruntun otomatis masuk daftar.
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <Badge variant="secondary" data-testid={`${testid}-cam-count`}>{sessionCount} discan</Badge>
              <div className="flex items-center gap-2">
                {torchSupported && (
                  <Button type="button" size="sm" variant={torchOn ? "default" : "outline"} onClick={toggleTorch} data-testid={`${testid}-cam-torch`}>
                    {torchOn ? <Zap className="w-4 h-4 sm:mr-1" /> : <ZapOff className="w-4 h-4 sm:mr-1" />}
                    <span className="hidden sm:inline">Senter</span>
                  </Button>
                )}
                <Button type="button" size="sm" variant="destructive" onClick={() => setCamOpen(false)} data-testid={`${testid}-cam-close`}>
                  <X className="w-4 h-4 mr-1" />Tutup Kamera
                </Button>
              </div>
            </div>

            {value.length > 0 && (
              <div className="flex flex-wrap gap-1.5 max-h-24 overflow-y-auto pt-1 border-t">
                {value.map((q) => (
                  <Badge key={q} variant="outline" className="font-mono text-[10px] gap-1 mt-2">
                    {q}
                    <button type="button" onClick={() => remove(q)}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
